import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRightIcon, BookOpenIcon, ChartBarIcon, ChatBubbleLeftRightIcon, ClipboardDocumentListIcon, Cog6ToothIcon, ExclamationTriangleIcon, InboxIcon, MagnifyingGlassIcon, UserCircleIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { getDashboard, listPatientsPage, type Dashboard as DashboardData, type Patient } from '../api/unimedica';
import { useAuth } from '../contexts/AuthContext';

// Port aus routes/+page.svelte — die Akten-zentrische Startseite.

type DeepHealth = { postgres?: { ok: boolean }; ollama?: { ok: boolean }; library?: { exists: boolean } };

const VORSCHLAEGE = [
  'Wer nimmt Metformin?',
  'Welche Akten haben seit einem Jahr keinen Eintrag?',
  'Dosierung Amlodipin bei Niereninsuffizienz',
];

const fmtDate = (iso: string | null) => {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso);
};
type AuthLike = { canSeeAkten: boolean; canSeeAdminArea: boolean; canUseMode: (m: any) => boolean };
const FUNKTIONEN: { href: string; titel: string; text: string; icon: any; sichtbar: (a: AuthLike) => boolean; zahl: (d: DashboardData | null) => number | null }[] = [
  { href: '/patienten', titel: 'Patienten', text: 'Akten öffnen, in der Stammliste suchen, Akten aus siMed holen. Diagnosen, Medikation, Labor und Dokumente auf einen Blick.', icon: UserGroupIcon, sichtbar: (a) => a.canSeeAkten, zahl: (d) => d ? d.patient_count : null },
  { href: '/eingang', titel: 'Eingang', text: 'Neu eingegangene Befunde und Dokumente prüfen, korrigieren und der richtigen Akte zuordnen.', icon: InboxIcon, sichtbar: (a) => a.canSeeAkten, zahl: (d) => d ? d.pending_count : null },
  { href: '/chat', titel: 'Fragen', text: 'Fragen an eine Akte oder über alle Akten hinweg, Wissen nachschlagen, Dokumente hochladen und zusammenfassen lassen.', icon: ChatBubbleLeftRightIcon, sichtbar: () => true, zahl: () => null },
  // #15 (Mario 10.10.): Arbeitstisch auch als Kachel
  { href: '/arbeitstisch', titel: 'Arbeitstisch', text: 'Visitenblatt, Brief, Foto oder Screenshot hineinlegen und daraus KG-Eintrag, Kurzfassung oder Brief machen — ohne Patient, nichts wird gespeichert.', icon: ClipboardDocumentListIcon, sichtbar: (a) => a.canSeeAkten, zahl: () => null },
  { href: '/chat?mode=praxis', titel: 'Praxisweite Auswertung', text: 'Kohorten und Listen: wer nimmt ein Medikament, wer hat auffällige Laborwerte, welche Akten sind lange unberührt.', icon: ChartBarIcon, sichtbar: (a) => a.canUseMode('praxis'), zahl: () => null },
  { href: '/chat?mode=wissen', titel: 'Wissen', text: 'Leitlinien, Studien und Fachinformationen nachschlagen — Compendium, PubMed und hausärztliche Quellen.', icon: BookOpenIcon, sichtbar: (a) => a.canUseMode('wissen'), zahl: () => null },
  { href: '/admin', titel: 'Verwaltung', text: 'Modelle, KI-Anweisungen, Werkzeuge, Nutzer einladen, Aktivitätsprotokoll.', icon: Cog6ToothIcon, sichtbar: (a) => a.canSeeAdminArea, zahl: () => null },
  { href: '/profil', titel: 'Profil & Benachrichtigungen', text: 'Eigene Daten und Benachrichtigungen auf dem Handy.', icon: UserCircleIcon, sichtbar: () => true, zahl: () => null },
];
const nameVon = (p: Patient) => [p.nachname, p.vorname].filter(Boolean).join(', ') || p.patient_id;

export default function Dashboard() {
  const auth = useAuth();
  const { user } = auth;
  const navigate = useNavigate();
  const [dash, setDash] = useState<DashboardData | null>(null);
  const [deepHealth, setDeepHealth] = useState<DeepHealth | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [frage, setFrage] = useState('');
  const [patienten, setPatienten] = useState<Patient[]>([]);
  const [feldAktiv, setFeldAktiv] = useState(false);

  useEffect(() => {
    const laden = async () => {
      try {
        setDash(await getDashboard());
        const r = await fetch('/api/health/deep');
        setDeepHealth(await r.json());
        setError(null);
      } catch (e) { setError(String(e)); }
    };
    void laden();
    const t = setInterval(() => void laden(), 60_000);
    return () => clearInterval(t);
  }, []);

  /** Stoerung nur zeigen, wenn wirklich etwas nicht geht — die Startseite ist kein Systemmonitor. */
  const stoerung = useMemo(() => {
    if (error) return 'Keine Verbindung zum Server. Bitte die IT informieren.';
    if (!deepHealth) return null;
    if (deepHealth.postgres?.ok === false) return 'Die Datenbank antwortet nicht — bitte die IT informieren.';
    if (deepHealth.library?.exists === false) return 'Die Patientenakten sind gerade nicht erreichbar.';
    if (deepHealth.ollama?.ok === false) return 'Fragen sind im Moment nicht möglich, die KI antwortet nicht. Akten lassen sich weiter öffnen.';
    return null;
  }, [error, deepHealth]);

  // #19: Kandidaten vom Server holen (vorher nur die ersten 200 Akten im Browser).
  useEffect(() => {
    const q = frage.trim().replace(/,/g, ' ');
    if (q.length < 2 || q.split(/\s+/).length > 3) { setPatienten([]); return; }
    const ab = new AbortController();
    const t = setTimeout(() => {
      listPatientsPage({ q, limit: 50, signal: ab.signal }).then((d) => setPatienten(d.patients)).catch(() => {});
    }, 250);
    return () => { clearTimeout(t); ab.abort(); };
  }, [frage]);

  /** Treffer nur bei echtem Namensanfang — sonst schlaegt jede Frage mit einem Namen eine Akte vor. */
  const treffer = useMemo(() => {
    const q = frage.trim().toLowerCase();
    if (q.length < 2) return [];
    return patienten.filter((p) => {
      const nach = (p.nachname ?? '').toLowerCase(), vor = (p.vorname ?? '').toLowerCase();
      return nach.startsWith(q) || vor.startsWith(q) || `${nach}, ${vor}`.startsWith(q) || p.patient_id.toLowerCase() === q;
    }).slice(0, 6);
  }, [frage, patienten]);

  const absenden = (text = frage) => {
    const q = text.trim();
    if (!q) return;
    if (treffer.length === 1 && text === frage) { navigate(`/patienten/${encodeURIComponent(treffer[0].patient_id)}`); return; }
    navigate(`/chat?q=${encodeURIComponent(q)}`);
  };

  const h = new Date().getHours();
  const gruss = h < 11 ? 'Guten Morgen' : h < 18 ? 'Guten Tag' : 'Guten Abend';
  const vorname = (user?.name ?? '').split(' ')[0];
  const heute = new Date().toLocaleDateString('de-CH', { weekday: 'long', day: 'numeric', month: 'long' });


  return (
    <div className="space-y-5">
      {stoerung && (
        <div className="card flex items-start gap-3 border-amber-400 bg-amber-50 px-4 py-3">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
          <span className="text-sm font-medium text-amber-800">{stoerung}</span>
        </div>
      )}

      {/* Begruessung + ein Feld fuer alles */}
      <div>
        <p className="text-xs uppercase tracking-wider text-ps-dark-400">{heute}</p>
        <h1 className="page-h1 mt-1">{gruss}{vorname ? `, ${vorname}` : ''}</h1>
        <p className="mt-1 text-sm text-ps-dark-500">Namen eingeben, um eine Akte zu öffnen — oder einfach eine Frage stellen.</p>

        <div className="relative mt-4 max-w-2xl">
          <div className="flex items-center gap-2 rounded-lg border-[1.5px] border-gray-300 bg-white py-1.5 pl-4 pr-1.5 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100">
            <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-ps-dark-400" />
            <input value={frage} onChange={(e) => setFrage(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); absenden(); } if (e.key === 'Escape') { setFrage(''); setFeldAktiv(false); } }}
              onFocus={() => setFeldAktiv(true)} onBlur={() => setTimeout(() => setFeldAktiv(false), 150)}
              className="min-w-0 flex-1 bg-transparent py-2.5 text-base text-ps-dark-900 placeholder:text-ps-dark-400 focus:outline-none"
              placeholder="Name oder Frage …" aria-label="Akte suchen oder Frage stellen" />
            <button onClick={() => absenden()} disabled={!frage.trim()}
              title={treffer.length === 1 ? 'Akte öffnen' : 'Fragen'}
              className="btn-primary min-h-11 shrink-0 px-3 disabled:cursor-not-allowed disabled:opacity-40 xs:px-5">
              <span className="hidden xs:inline">{treffer.length === 1 ? 'Akte öffnen' : 'Fragen'}</span>
              <ArrowRightIcon className="h-5 w-5 xs:hidden" />
            </button>
          </div>
          {feldAktiv && treffer.length > 0 && (
            <ul className="absolute z-20 mt-1.5 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
              {treffer.map((p) => (
                <li key={p.patient_id}>
                  <Link to={`/patienten/${encodeURIComponent(p.patient_id)}`} className="flex min-h-11 items-center gap-3 px-4 py-2.5 hover:bg-primary-50">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-ps-dark-700">{(p.nachname ?? p.patient_id).slice(0, 2).toUpperCase()}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-ps-dark-900">{nameVon(p)}</span>
                      <span className="block text-xs text-ps-dark-400">{p.geburtsdatum ? fmtDate(p.geburtsdatum) : 'Geburtsdatum unbekannt'} · {p.doc_count} {p.doc_count === 1 ? 'Dokument' : 'Dokumente'}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {!frage.trim() && (
          <div className="mt-3 flex flex-wrap gap-2">
            {VORSCHLAEGE.map((v) => (
              <button key={v} onClick={() => { setFrage(v); absenden(v); }}
                className="rounded-full border border-gray-200 bg-white px-3.5 py-1.5 text-sm text-ps-dark-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700">{v}</button>
            ))}
          </div>
        )}
      </div>

      {/* Funktionen auf einen Blick (Christian 08.09.: „zügig auf alle Funktionen zugreifen, mit kurzer
          Beschreibung" — keine Dokumentlisten mehr). Sichtbar nur, was die Rolle darf. */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {FUNKTIONEN.filter((f) => f.sichtbar(auth)).map((f) => (
          <Link key={f.href} to={f.href} className="card group flex flex-col gap-2 border-gray-200 p-4 transition hover:border-primary-300 hover:shadow-md">
            <div className="flex items-center justify-between">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-600 group-hover:bg-primary-100"><f.icon className="h-5 w-5" /></span>
              {f.zahl(dash) !== null && <span className="numerical rounded-full bg-ps-dark-100 px-2 py-0.5 text-[11.5px] font-semibold text-ps-dark-700">{f.zahl(dash)}</span>}
            </div>
            <div className="text-[15px] font-semibold text-ps-dark-900">{f.titel}</div>
            <p className="text-[12.5px] leading-relaxed text-ps-dark-500">{f.text}</p>
            {f.href === '/eingang' && dash && dash.pending_count > 0 && (
              <ul className="mt-1 space-y-0.5 border-t border-gray-100 pt-2 text-[12px] text-ps-dark-600">
                {dash.pending_items.slice(0, 3).map((item) => <li key={item.ref} className="truncate">• {item.title}</li>)}
              </ul>
            )}
            {f.href === '/chat' && dash && dash.recent_chats.length > 0 && (
              <ul className="mt-1 space-y-0.5 border-t border-gray-100 pt-2 text-[12px] text-ps-dark-600">
                {dash.recent_chats.slice(0, 3).map((c) => <li key={c.id} className="truncate">• {c.title}</li>)}
              </ul>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
